import React, {
  forwardRef,
  memo,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { formatTranslation, getTranslate } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { mergeSx } from 'merge-sx';
import { useIntl } from 'react-intl';
import { useProtocolItemStatus } from 'components/common/withProtocolItemStatus';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { withDefaultComplianceOptionLabels } from 'shared/domain/compliance/complianceOptionLabels';
import { ComplianceOptionLabelModel } from 'shared/domain/template/inspectionTemplateModel';
import {
  complianceButtonSx,
  complianceToggleButtonTextSx,
  complianceToggleGroupSx,
  getComplianceButtonStyleForOption,
} from 'shared/domain/compliance/complianceButtonStyles';

const COMPLIANCE_CHECK_PREFIX = 'inspection-compliance-check-item';

interface ProtocolItemStatusSelectorProps {
  checklistItemId: string;
  handleChange: (_: any, value: ComplianceAnswerOption) => void;
  value: any;
  optionLabels?: ComplianceOptionLabelModel[];
  disabled?: boolean;
}

function ComplianceAnswerOptionSelector(
  {
    checklistItemId,
    handleChange,
    value,
    optionLabels,
    disabled,
  }: ProtocolItemStatusSelectorProps,
  ref: Ref<HTMLDivElement>
): ReactElement {
  const intl = useIntl();
  const { setStatus } = useProtocolItemStatus();

  // Lifts the answer to the protocol item's status provider (its header icon).
  useEffect(() => {
    setStatus(value || ComplianceAnswerOption.unset);
  }, [value, setStatus]);

  const selfHandleChange = useCallback(
    (_, value: ComplianceAnswerOption | undefined) => {
      handleChange(_, value || ComplianceAnswerOption.unset);
    },
    [handleChange]
  );

  const resolvedOptionLabels = useMemo(() => {
    if (optionLabels && optionLabels.length > 0) {
      return withDefaultComplianceOptionLabels(optionLabels, getTranslate(intl));
    }
    return [
      { option: ComplianceAnswerOption.passed, label: formatTranslation(intl, CommonTranslationKeys.yes) },
      { option: ComplianceAnswerOption.notPassed, label: formatTranslation(intl, CommonTranslationKeys.no) },
    ];
  }, [optionLabels, intl]);

  const displayOptions = useMemo(() => {
    return resolvedOptionLabels.filter(
      (ol) =>
        ol.option !== ComplianceAnswerOption.unset &&
        ol.option !== ComplianceAnswerOption.notApplicable
    );
  }, [resolvedOptionLabels]);

  return (
    <ToggleButtonGroup
      ref={ref}
      id={`${COMPLIANCE_CHECK_PREFIX}-${checklistItemId}`}
      color='primary'
      aria-label='outlined primary button group'
      sx={complianceToggleGroupSx}
      value={value}
      exclusive
      onChange={selfHandleChange}
      disabled={disabled}
    >
      {displayOptions.map((optionLabel) => (
        <ToggleButton
          key={optionLabel.option}
          disableRipple
          sx={mergeSx(complianceButtonSx, getComplianceButtonStyleForOption(optionLabel.option))}
          value={optionLabel.option}
        >
          <Box component='span' sx={complianceToggleButtonTextSx}>
            {optionLabel.label}
          </Box>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export const MemoComplianceAnswerOptionSelector = memo(
  forwardRef(ComplianceAnswerOptionSelector)
);
